import {
  DateHelper,
  PresetManager,
  StringHelper,
  TaskModel,
} from '@bryntum/gantt';
import moment from 'moment';
import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import zipcelx from 'zipcelx';
import { ProjectPhase } from 'api/project';
import { ProjectsReportAPI } from 'api/projects-report';
import { Field, FieldValue, FieldValueType } from 'api/tasks';
import { humanise } from 'components/atoms/humanised-date';
import { PermissionRuleName } from 'constant';
import { useProjectPhaseStatusField, useStatusField } from 'data/fields/hooks';
import { Task } from 'data/tasks';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useTranslate } from 'hooks/use-translation';
// eslint-disable-next-line import/no-internal-modules
import { ApiKey } from 'services';
import { Entity, getCurrentDuration, List } from 'utils/date-shift';
import { TimelineFilterZoomLevel } from '../constants';
import {
  formatDate,
  GANTT_TOOLTIP_DATE_FORMAT,
  GanttType,
  getDueDateFromGantt,
} from '../helpers';
import { TaskDetail } from '../task-detail';

const getChildNodes = (record: TaskModel): List => {
  const list: List = {};

  const getChildren = (_record: TaskModel) => {
    const children = _record.children as TaskModel[];

    children?.forEach((childRecord) => {
      const recordData = childRecord.get('data');

      list[recordData.id] = {
        ...recordData,
        startDate: formatDate(childRecord.get('startDate')),
        dueDate: getDueDateFromGantt(childRecord),
      } as Entity;

      getChildren(childRecord);
    });
  };

  getChildren(record);

  return list;
};

export const useGanttStaticConfig = ({ ganttRef }: { ganttRef: any }) => {
  const translate = useTranslate();
  const projectPhaseStatusField = useProjectPhaseStatusField();
  const taskStatusField = useStatusField();

  const getStatusFieldDetails = useCallback(
    (
      record: TaskModel,
    ): {
      statusField: Field;
      statusFieldValue: FieldValue<FieldValueType> | undefined;
    } => {
      const recordData: Task | ProjectPhase = record.get('data');

      if (record.getData('type') === GanttType.ProjectPhaseBar) {
        return {
          statusField: projectPhaseStatusField,
          statusFieldValue: recordData.fields?.find(
            (f) => f.fieldId === projectPhaseStatusField?.fieldId,
          ),
        };
      }

      return {
        statusField: taskStatusField,
        statusFieldValue: recordData.fields?.find(
          (f) => f.fieldId === taskStatusField?.fieldId,
        ),
      };
    },
    [projectPhaseStatusField, taskStatusField],
  );

  useEffect(() => {
    if (!ganttRef) {
      return;
    }
    ganttRef?.instance?.features.taskDrag.setConfig({
      tooltipTemplate: (data) => {
        const clockTemplate =
          ganttRef?.instance.features.taskDrag.clockTemplate;

        return `
          <div class="b-sch-tip-${data.valid ? 'valid' : 'invalid'}">
            ${data.startClockHtml}
            ${clockTemplate.template({
              text: getDueDateFromGantt(data, GANTT_TOOLTIP_DATE_FORMAT),
              cls: 'b-sch-tooltip-enddate',
            })}
          </div>
        `;
      },
    });

    ganttRef?.instance?.features.dependencies.tooltip.setConfig({
      getHtml: ({ activeTarget }) => {
        const dependency = ganttRef?.instance.dependencyStore.getById(
          activeTarget.getAttribute('depId'),
        );

        return `
          <table class="b-sch-dependency-tooltip">
            <tr>
                <td>${translate('from')}: </td>
                <td>${dependency?.fromEvent.name}</td>
            </tr>
            <tr>
                <td>${translate('to')}: </td>
                <td>${dependency?.toEvent.name}</td>
            </tr>
        </table>
        `;
      },
    });
  }, [ganttRef, translate]);
  const { isAllowedForCurrentUser } = usePermissionForRule();

  return useMemo(
    () => ({
      columns: [
        {
          type: 'name',
          width: 320,
          field: 'title',
          text: translate('projects.plan'),
          renderer: ({
            record,
            value,
            isExport,
          }: {
            record: TaskModel;
            value: string;
            isExport: boolean;
          }): string | ReactNode => {
            if (isExport) {
              return value;
            }

            // return value;
            return <TaskDetail record={record} value={value} />;
          },
        },
        {
          type: 'date',
          text: translate('start_date'),
          field: 'startDate',
          width: 89,
          renderer: ({ record }: { record }): string =>
            record.get('isCalculatedStartDate')
              ? ''
              : humanise(moment(record.getStartDate())),
        },
        {
          type: 'date',
          text: translate('due_date'),
          field: 'endDate',
          width: 89,
          renderer: ({ record }: { record }): string => {
            if (record.get('isCalculatedEndDate')) {
              return '';
            }

            return humanise(moment(getDueDateFromGantt(record)));

            /**
             * Gantt re-renders twice, once with correct state and again with old state data
             * hence rendering a simple string
             * TODO: fix this issue to render the following custom component.
             */
            // let isCompleted;

            // if (record.getData('type') === GanttType.ProjectPhaseBar) {
            //   const { statusFieldValue } = getStatusFieldDetails(record);

            //   isCompleted =
            //     statusFieldValue?.metaFieldValue?.group === StatusGroup.Completed;
            // } else {
            //   isCompleted = Boolean(record.get('data')?.completedAt);
            // }

            // return (
            //   <DueDate
            //     timestamp={moment(record.getEndDate())}
            //     completed={isCompleted}
            //   />
            // );
          },
        },
        {
          type: 'duration',
          renderer: ({ record }: { record }): string => {
            const displayDuration = getCurrentDuration(
              record.get('data').id,
              formatDate(record.getStartDate()),
              getDueDateFromGantt(record),
              getChildNodes(record),
            );

            // const displayDuration = getDuration(
            //   record.getStartDate(),
            //   getDueDateFromGantt(record),
            // );

            return `${displayDuration}d`;
          },
        },
      ],
      zoomLevel: TimelineFilterZoomLevel.Week,
      maxZoomLevel: TimelineFilterZoomLevel.Day,
      minZoomLevel: TimelineFilterZoomLevel.Quarter,
      zoomOnMouseWheel: false,
      zoomOnTimeAxisDoubleClick: false,
      zoomKeepsOriginalTimespan: true,
      nonWorkingTimeFeature: false,
      percentBarFeature: {
        disabled: false,
        allowResize: false,
      },
      timeRangesFeature: {
        currentDateFormat: 'hh:mm A',
        showCurrentTimeLine: false,
      },
      labelsFeature: {
        right: {
          renderer: ({ taskRecord }) => {
            const { name, assignees, type } = taskRecord.get('data');
            const isMilestone = type === GanttType.MilestoneBar;

            if (type === 'phase') {
              return `<label class='text-02'>${renderToStaticMarkup(
                name,
              )}</label>`;
            }
            let assigneeNamesForTooltip = '';
            let assigneeNamesForMarkup = '';

            assignees.forEach(({ firstName, lastName }) => {
              assigneeNamesForTooltip += `${firstName ?? ''} ${
                lastName ?? ''
              }\n`;
            });

            if (assignees?.length > 0) {
              const { firstName, lastName } = assignees[0];

              assigneeNamesForMarkup += `${firstName ?? ''} ${lastName ?? ''}`;
              if (assignees.length > 1) {
                assigneeNamesForMarkup += ` + ${assignees.length - 1} other(s)`;
              }
            }

            return `
              <label class='text-02' style='display: flex; position: relative;'>
                ${
                  isMilestone
                    ? '<label title="Milestone" class="gantt-type-milestone"></label>'
                    : ''
                }
                <span style='margin: 0 8px 0 2px;'>${renderToStaticMarkup(
                  name,
                )}</span>
                ${
                  assignees.length > 0
                    ? `
                    &bull;
                    <span style='margin-left: 8px;' title='${renderToStaticMarkup(
                      <>{assigneeNamesForTooltip.trim()}</>,
                    )}'>${renderToStaticMarkup(
                        <>{assigneeNamesForMarkup}</>,
                      )}</span>
                    `
                    : ''
                }
              </label>
            `;
          },
        },
      },
      presets: PresetManager.map((preset) => {
        return {
          base: preset.id,
          displayDateFormat: 'MMM DD, YYYY',
        };
      }),
      features: {
        pdfExport: {
          exportServer: ProjectsReportAPI.getTimelinePdfExportUrl(),
          fetchOptions: {
            headers: ApiKey.header(),
          },
        },
        excelExporter: {
          zipcelx,
          dateFormat: 'YYYY-MM-DD',
          exporterConfig: {
            columns: [
              {
                text: 'WBS',
                type: 'wbs',
                renderer: ({ record }) => record.wbsCode,
              },
              'title',
              {
                text: 'Status',
                renderer: ({ record }) => {
                  const { statusFieldValue } = getStatusFieldDetails(record);

                  return statusFieldValue?.metaFieldValue?.label;
                },
              },
              'startDate',
              {
                text: 'Due date',
                type: 'string',
                renderer: ({ record }) => {
                  return getDueDateFromGantt(record);
                },
              },
              {
                text: 'Duration',
                type: 'number',
                renderer: ({ record }) => {
                  // const duration = getDuration(
                  //   record.getStartDate(),
                  //   record.getEndDate(),
                  // );

                  const duration = getCurrentDuration(
                    record.get('data').id,
                    formatDate(record.getStartDate()),
                    getDueDateFromGantt(record),
                    getChildNodes(record),
                  );

                  return `${duration} ${DateHelper.getLocalizedNameOfUnit(
                    record.durationUnit,
                    record.duration !== 1,
                  )}`;
                },
              },
              {
                text: 'Assignee(s)',
                renderer: ({ record }) => {
                  const { assignees, type } = record.get('data');

                  if (type === 'phase') {
                    return null;
                  }
                  let names = '';

                  assignees.forEach(({ firstName, lastName }) => {
                    names += `${firstName} ${lastName}\n`;
                  });

                  return names.trim();
                },
              },
            ],
          },
        },
        taskResize: {
          /**
           * end date in tooltip on resize appears wrong
           * hence hiding tooltip.
           * TODO: get the correct end date displayed in tooltip.
           */
          showTooltip: false,
        },

        taskTooltip: {
          template(data) {
            /**
             * Below template is nothing but gantt default tooltip template
             * except a logic update to derive duration
             * since duration is not able to be updated through any other config
             * and it will be harder to maintain selective weekends as working days
             * via calendar config, hence to fix the duration count
             * and keep the rest of the template as it is
             * the template is copied from gantt.
             */
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const me: any = this;
            const { taskRecord } = data;
            // const displayDuration = getDuration(
            //   taskRecord.getStartDate(),
            //   getDueDateFromGantt(taskRecord),
            // );

            const displayDuration = getCurrentDuration(
              taskRecord.get('data').id,
              formatDate(taskRecord.getStartDate()),
              getDueDateFromGantt(taskRecord),
              getChildNodes(taskRecord),
            );

            return `
            ${
              taskRecord.name
                ? `<div class="b-gantt-task-title">${StringHelper.encodeHtml(
                    taskRecord.name,
                  )}</div>`
                : ''
            }
            <table border="0" cellspacing="0" cellpadding="0">
              <tr>
                <td>${me.L('L{Start}')}:</td>
                <td>${data.startClockHtml}</td>
              </tr>
              ${
                taskRecord.milestone
                  ? ''
                  : `
                <tr>
                  <td>${me.L('L{End}')}:</td>
                  <td>${getDueDateFromGantt(
                    taskRecord,
                    GANTT_TOOLTIP_DATE_FORMAT,
                  )}</td>
                </tr>
                <tr>
                  <td>${me.L('L{Duration}')}:</td>
                  <td class="b-right">
                    ${`${displayDuration} ${DateHelper.getLocalizedNameOfUnit(
                      taskRecord.durationUnit,
                      taskRecord.duration !== 1,
                    )}`}
                  </td>
                </tr>
                <tr>
                  <td>${me.L('L{Complete}')}:</td>
                  <td class="b-right">${
                    taskRecord.get('data').progress ?? 0
                  }%</td>
                </tr>`
              }
            </table>
          `;
          },
        },
        dependencies: {
          allowCreate: isAllowedForCurrentUser(
            PermissionRuleName.TaskAddDependentTaskAction,
          ),
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ganttRef],
  );
};
